import { DEFAULT_EVENT_FILTERS } from 'components/Events/Filters/eventFilters'
import { useCallback } from 'react'
import { useLocation } from 'react-router-dom'

import { useSearchParam } from 'hooks/useSearchParams'

//  * Hook for working with events filters
//  * @returns {Object} - Object with functions to convert filters to query params and vice versa
//  *

const FILTER_QUERY_KEYS = [
  'q',
  'simulated',
  'since',
  'until',
  'includePastEvents',
  'city',
  'vh',
  'marketing_campaign_status',
  'frontend_status',
]

export const useEventsFilter = () => {
  const { get: getParam, setAll, clearMany } = useSearchParam()
  const location = useLocation()

  const filtersToQueryParams = useCallback((filters) => {
    const queryParams = {}
    if (filters.search) queryParams.q = filters.search
    if (filters.source) queryParams.simulated = filters.source
    if (filters?.dates?.from && filters.dates.from !== DEFAULT_EVENT_FILTERS.dates.from) queryParams.since = filters.dates.from
    if (filters?.dates?.to && filters.dates.to !== DEFAULT_EVENT_FILTERS.dates.to) queryParams.until = filters.dates.to
    if (filters.includePastEvents === true) queryParams.includePastEvents = '1'

    const cityTitles = (filters.cities || []).map((c) => c.title).filter(Boolean)
    if (cityTitles.length) queryParams.city = cityTitles.join(',')

    const venuesAndHallsParam = (filters.venuesAndHalls || [])
      .map((item) => `${item.venue ?? ''};;${item.hall ?? ''}`)
      .filter(Boolean)
    if (venuesAndHallsParam.length) queryParams.vh = venuesAndHallsParam.join(',')

    const marketingStatuses = (filters.campaigns || []).filter(Boolean)
    if (marketingStatuses.length) queryParams.marketing_campaign_status = marketingStatuses.join(',')

    const frontendStatuses = (filters?.status?.value || []).filter(Boolean)
    const defaultFrontendStatuses = (DEFAULT_EVENT_FILTERS?.status?.value || [])
    const isDefaultFrontendStatuses = frontendStatuses.length === defaultFrontendStatuses.length && frontendStatuses.every((v, i) => v === defaultFrontendStatuses[i])
    if (frontendStatuses.length && !isDefaultFrontendStatuses) queryParams.frontend_status = frontendStatuses.join(',')

    return queryParams
  }, [])

  const applyQueryParams = useCallback((queryParams) => {
    // Read from window.location.search to avoid stale React Router location during history.replace races
    const currentSearch = typeof window !== 'undefined' && window.location ? window.location.search : location.search
    const params = new URLSearchParams(currentSearch)

    FILTER_QUERY_KEYS.forEach((key) => params.delete(key))

    Object.entries(queryParams || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.set(key, String(value))
      }
    })

    setAll(Object.fromEntries(params.entries()))
  }, [location.search, setAll])


  const queryParamsToFilters = useCallback(() => {
    const query = getParam('q')
    const simulated = getParam('simulated')
    const since = getParam('since')
    const until = getParam('until')
    const includePastEvents = getParam('includePastEvents')
    const citiesCsv = getParam('city')
    const venuesAndHallsCsv = getParam('vh')
    const marketingStatusesCsv = getParam('marketing_campaign_status')
    const frontendStatusesCsv = getParam('frontend_status')

    const parsed = {}
    if (query) parsed.search = query
    if (simulated) parsed.source = simulated
    if (since || until) {
      parsed.dates = {
        from: since || DEFAULT_EVENT_FILTERS.dates.from,
        to: until || DEFAULT_EVENT_FILTERS.dates.to,
        hasCustomDate: Boolean(since || until),
      }
    }
    if (includePastEvents === '1' || includePastEvents === '0') {
      parsed.includePastEvents = includePastEvents === '1'
    }
    if (citiesCsv) {
      parsed.cities = citiesCsv.split(',').filter(Boolean).map((title) => ({ title }))
    }
    if (venuesAndHallsCsv) {
      parsed.venuesAndHalls = venuesAndHallsCsv.split(',').map((pair) => {
        const [venue, hall] = pair.split(';;')
        return { venue: venue || '', hall: hall || '' }
      })
    }
    if (marketingStatusesCsv) parsed.campaigns = marketingStatusesCsv.split(',').filter(Boolean)
    if (frontendStatusesCsv) parsed.status = { isDefault: false, value: frontendStatusesCsv.split(',').filter(Boolean) }

    return Object.keys(parsed).length ? parsed : null
  }, [getParam])


  const clearAllQueryParams = useCallback(() => {
    clearMany(FILTER_QUERY_KEYS)
  }, [clearMany])

  return { 
    applyQueryParams, 
    filtersToQueryParams, 
    queryParamsToFilters, 
    clearAllQueryParams,
  }
}